import React, { useState, useCallback } from 'react';
import { Input, Modal, message, Button as AntButton } from 'antd';
import { AppstoreOutlined } from '@ant-design/icons';
import { Pencil, Trash2 } from 'lucide-react';
import styled from 'styled-components';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { ContentCard, MutedText, PanelSectionLabel } from '@/pages/inside/tenant/style';

const ActionButton = styled(AntButton)`
    height: 22px !important;
    min-height: 22px !important;
    width: 24px !important;
    padding: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    transition: all 0.2s ease-in-out !important;
    box-shadow: none !important;
    border-radius: 4px !important;
    background-color: transparent !important;
    border: 1px solid var(--ant-color-border, rgba(0, 0, 0, 0.15)) !important;
    color: var(--ant-color-text-description, rgba(0, 0, 0, 0.45)) !important;
    
    && svg {
        width: 12px !important;
        height: 12px !important;
        color: inherit !important;
        stroke: currentColor !important;
        fill: none !important;
        transition: all 0.2s ease-in-out !important;
    }
    
    &&:hover:not(:disabled):not(.ant-btn-disabled) {
        background-color: ${({ $isDanger }) => $isDanger ? 'rgba(220, 38, 38, 0.08)' : 'rgba(117, 67, 220, 0.08)'} !important;
        color: ${({ $isDanger }) => $isDanger ? '#dc2626' : '#7543DC'} !important;
        border: 1px solid ${({ $isDanger }) => $isDanger ? 'rgba(220, 38, 38, 0.3)' : 'rgba(117, 67, 220, 0.3)'} !important;
    }
`;

function DeliverableRows({
    deliverables,
    milestoneId,
    permissionDenied,
    togglePending,
    onToggleDeliverable,
    onEditDeliverable,
    onDeleteDeliverable,
    editPending,
    deletePending,
    maxHeight,
}) {
    const [hoveredId, setHoveredId] = useState(null);

    return (
        <Container
            $display="flex"
            $flexDirection="column"
            $gap="8px"
            style={maxHeight ? { maxHeight, overflowY: 'auto' } : undefined}
        >
            {deliverables.map((d, idx) => (
                <Container
                    key={String(d.id ?? `deliverable-${idx}`)}
                    $display="flex"
                    $alignItems="center"
                    $gap="12px"
                    style={{
                        padding: '10px 12px',
                        paddingRight: hoveredId === d.id ? '76px' : '12px',
                        borderRadius: 8,
                        background: hoveredId === d.id ? 'var(--ant-color-fill-tertiary, rgba(0,0,0,0.04))' : 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                        position: 'relative',
                        transition: 'all 0.15s ease-in-out',
                    }}
                    onMouseEnter={() => setHoveredId(d.id)}
                    onMouseLeave={() => setHoveredId(null)}
                >
                    <Checkbox
                        checked={d.isMarked}
                        disabled={permissionDenied || togglePending}
                        variant="success"
                        style={{ flexShrink: 0 }}
                        onChange={(e) => {
                            if (permissionDenied || !milestoneId || togglePending) return;
                            onToggleDeliverable(milestoneId, d.id, e.target.checked);
                        }}
                    />
                    <span
                        style={{
                            fontSize: 14,
                            lineHeight: '20px',
                            textDecoration: d.isMarked ? 'line-through' : 'none',
                            opacity: d.isMarked ? 0.6 : 0.9,
                            color: 'var(--ant-color-text)',
                            flex: 1,
                            minWidth: 0,
                            wordBreak: 'break-word',
                        }}
                    >
                        {d.value || '—'}
                    </span>
                    {hoveredId === d.id && !permissionDenied && (
                        <div style={{
                            position: 'absolute',
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            display: 'flex',
                            gap: '4px',
                            background: 'var(--ant-color-bg-elevated, #ffffff)',
                            border: '1px solid var(--ant-color-border-secondary, rgba(0, 0, 0, 0.06))',
                            padding: '3px',
                            borderRadius: '6px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            zIndex: 10,
                        }}>
                            <ActionButton
                                type="button"
                                icon={<Pencil />}
                                loading={editPending}
                                onClick={() => onEditDeliverable(milestoneId, d.id, d.value)}
                                title="Edit deliverable"
                            />
                            <ActionButton
                                type="button"
                                icon={<Trash2 />}
                                loading={deletePending}
                                onClick={() => onDeleteDeliverable(milestoneId, d.id)}
                                title="Delete deliverable"
                                $isDanger
                            />
                        </div>
                    )}
                </Container>
            ))}
        </Container>
    );
}

const ADD_BUTTON_MOTION = { $entryAnimation: false, $hoverAnimation: false, $hoverShadow: false };

/**
 * API deliverables: list on the card (GET milestone.deliverables); add flow opens a modal that stays open after each add.
 */
export default function MilestoneDeliverablesPanel({
    milestoneId,
    deliverables,
    canEditMilestone,
    onAddDeliverable,
    onToggleDeliverable,
    onEditDeliverable,
    onDeleteDeliverable,
    addPending,
    togglePending,
    editPending,
    deletePending,
}) {
    const permissionDenied = !canEditMilestone;
    const [modalOpen, setModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingDeliverable, setEditingDeliverable] = useState(null);
    const [draft, setDraft] = useState('');
    const [editDraft, setEditDraft] = useState('');

    const doneCount = deliverables.filter((d) => d.isMarked).length;
    const total = deliverables.length;

    const submit = useCallback(() => {
        const v = draft.trim();
        if (!v || !milestoneId) return;
        onAddDeliverable(milestoneId, v);
        setDraft('');
    }, [draft, milestoneId, onAddDeliverable]);

    const handleOpenEdit = useCallback(
        (mId, dId, currentValue) => {
            setEditingDeliverable({ milestoneId: mId, deliverableId: dId });
            setEditDraft(currentValue || '');
            setEditModalOpen(true);
        },
        [],
    );

    const handleEditSubmit = useCallback(() => {
        if (!editingDeliverable) return;
        const { milestoneId: mId, deliverableId: dId } = editingDeliverable;
        const v = editDraft.trim();
        if (!v) {
            message.warning('Enter a valid deliverable text');
            return;
        }
        onEditDeliverable(mId, dId, v);
        setEditModalOpen(false);
        setEditingDeliverable(null);
        setEditDraft('');
    }, [editingDeliverable, editDraft, onEditDeliverable]);

    const handleDeleteConfirm = useCallback(
        (mId, dId) => {
            Modal.confirm({
                title: 'Delete Deliverable',
                content: 'Are you sure you want to delete this deliverable?',
                okText: 'Delete',
                okType: 'danger',
                cancelText: 'Cancel',
                onOk: () => {
                    onDeleteDeliverable(mId, dId);
                },
            });
        },
        [onDeleteDeliverable],
    );

    const summary =
        total > 0 ? (
            <MutedText style={{ margin: '12px 0 0', fontSize: 14 }}>
                {doneCount} of {total} completed
            </MutedText>
        ) : null;

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    marginBottom: 10,
                    flexWrap: 'wrap',
                }}
            >
                <PanelSectionLabel style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 0 }}>
                    <AppstoreOutlined />
                    Deliverables
                </PanelSectionLabel>
                {canEditMilestone ? (
                    <Button
                        type="button"
                        $primary
                        $blackOnLight
                        $size="13px"
                        $padding="8px 16px"
                        style={{ flexShrink: 0 }}
                        {...ADD_BUTTON_MOTION}
                        onClick={() => setModalOpen(true)}
                    >
                        Add deliverable
                    </Button>
                ) : null}
            </div>
            <ContentCard $padding="12px 16px" $marginBottom="0">
                {total === 0 ? (
                    <MutedText style={{ margin: 0 }}>No deliverables yet.</MutedText>
                ) : (
                    <DeliverableRows
                        deliverables={deliverables}
                        milestoneId={milestoneId}
                        permissionDenied={permissionDenied}
                        togglePending={togglePending}
                        onToggleDeliverable={onToggleDeliverable}
                        onEditDeliverable={handleOpenEdit}
                        onDeleteDeliverable={handleDeleteConfirm}
                        editPending={editPending}
                        deletePending={deletePending}
                        maxHeight={360}
                    />
                )}
                {summary}
            </ContentCard>

            <Modal
                open={modalOpen}
                title="Add deliverable"
                onCancel={() => {
                    setModalOpen(false);
                    setDraft('');
                }}
                footer={null}
                destroyOnHidden={false}
                width={520}
                styles={{ body: { paddingTop: 8 } }}
            >
                {canEditMilestone ? (
                    <Container $display="flex" $flexDirection="column" $gap="10px">
                        <Input.TextArea
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            placeholder="Set up project repository and CI pipeline…"
                            autoSize={{ minRows: 3, maxRows: 8 }}
                            disabled={permissionDenied}
                            maxLength={200}
                            showCount
                            onPressEnter={(e) => {
                                if (!e.shiftKey) {
                                    e.preventDefault();
                                    submit();
                                }
                            }}
                        />
                        <Button
                            type="button"
                            $primary
                            $blackOnLight
                            $size="13px"
                            $padding="8px 14px"
                            loading={addPending}
                            {...ADD_BUTTON_MOTION}
                            disabled={!draft.trim()}
                            disabledTooltip={!draft.trim() ? 'Enter text first' : undefined}
                            onClick={submit}
                            style={{ alignSelf: 'flex-start' }}
                        >
                            Add deliverable
                        </Button>
                    </Container>
                ) : null}
            </Modal>

            <Modal
                open={editModalOpen}
                title="Edit deliverable"
                onCancel={() => {
                    setEditModalOpen(false);
                    setEditingDeliverable(null);
                    setEditDraft('');
                }}
                footer={null}
                destroyOnHidden={false}
                width={520}
                styles={{ body: { paddingTop: 8 } }}
            >
                <Container $display="flex" $flexDirection="column" $gap="10px">
                    <Input.TextArea
                        value={editDraft}
                        onChange={(e) => setEditDraft(e.target.value)}
                        placeholder="Update deliverable text…"
                        autoSize={{ minRows: 3, maxRows: 8 }}
                        maxLength={200}
                        showCount
                        onPressEnter={(e) => {
                            if (!e.shiftKey) {
                                e.preventDefault();
                                handleEditSubmit();
                            }
                        }}
                    />
                    <Button
                        type="button"
                        $primary
                        $blackOnLight
                        $size="13px"
                        $padding="8px 14px"
                        loading={editPending}
                        {...ADD_BUTTON_MOTION}
                        disabled={!editDraft.trim()}
                        disabledTooltip={!editDraft.trim() ? 'Enter text first' : undefined}
                        onClick={handleEditSubmit}
                        style={{ alignSelf: 'flex-start' }}
                    >
                        Update deliverable
                    </Button>
                </Container>
            </Modal>
        </div>
    );
}
